import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  type DownloadComplete,
  type DownloadNotComplete,
  type DownloadRequestBody,
  type DownloadState,
  DownloadStatus, // enum
} from '../../../../types/isomorphic/downloadRequest.types';
import useApi from '../useApi';
import downloadFileFromUrl from '../../../utils/downloadFileFromUrl';
import useImageDownloadSizes from './useImageDownloadSizesRequest';
import useDownloadModifiedAsset from '../../../hooks/useDownloadModifiedAsset';
import isCompleteDownloadRequest from '../../../utils/typeGuards';
import queryKeys from '../../../support/uiStateKeys';

/**
 * Helper function to generate a unique and consistent cache key
 */
export const getDownloadKey = (genRequestId: string, index: number) =>
  [
    queryKeys.REMOVE_QUERY_ON_MODAL_CLOSE,
    'download',
    genRequestId,
    index,
  ] as const;

export const POLLING_TIME_OUT = 1000 * 60 * 2; // 2 minutes
const POLLING_INTERVAL = 2000; // 2 seconds
const FAILED_RESPONSE = { status: DownloadStatus.FAILED };

/**
 * Helper function to determine if the download has timed out
 */
const isTimeOut = (startedAt?: number): boolean => {
  const elapsedTime = startedAt ? Date.now() - startedAt : 0;
  return elapsedTime > POLLING_TIME_OUT;
};

/**
 * Helper function to determine if the download should be or is currently polling
 */
const shouldContinuePolling = (data?: DownloadState): boolean =>
  !!data &&
  data.status === DownloadStatus.UPSCALING &&
  !isTimeOut(data.startedAt);

/**
 * Get the download state for a given genRequestId and index
 */
export const useDownloadData = ({ genRequestId, index }): DownloadState => {
  const { downloadService } = useApi();
  const queryClient = useQueryClient();
  const queryKey = getDownloadKey(genRequestId, index);

  const { data, isError } = useQuery({
    queryKey,
    queryFn: async () => {
      /** Preserve startedAt from cache if it exists */
      const cachedData: DownloadState | null | undefined =
        queryClient.getQueryData(queryKey);
      const startedAt = cachedData?.startedAt ?? Date.now();

      if (!cachedData) {
        return null;
      }

      if (isCompleteDownloadRequest(cachedData)) {
        return cachedData;
      }

      const response = await downloadService.pollForDownload(
        genRequestId,
        index
      );
      /**
       * If the download is complete in the response but not in the cache,
       * !isCompleteDownloadRequest(cachedData) will prevent subsequent downloads when accessing the cached data
       */
      if (
        isCompleteDownloadRequest(response) &&
        !isCompleteDownloadRequest(cachedData)
      ) {
        downloadFileFromUrl(document, response.url);
      }

      if (isTimeOut(startedAt)) {
        return FAILED_RESPONSE;
      }

      return {
        startedAt,
        ...response,
      };
    },
    initialData: null,
    /** Polling interval is enabled when the download is in progress */
    refetchInterval: (query) =>
      shouldContinuePolling(query.state.data) ? POLLING_INTERVAL : false,
    refetchIntervalInBackground: true,
  });

  if (isError) {
    return FAILED_RESPONSE;
  }

  return data;
};

/**
 * useMutation hook to initiate a download request.
 * It will also handle the cache updates and download file from URL if the download is complete.
 * If doesn't not respond with a complete download, it will trigger useDownloadData to start polling.
 */
export const useDownloadMutation = (): UseMutationResult<
  DownloadComplete | DownloadNotComplete,
  Error,
  DownloadRequestBody
> => {
  const { downloadService } = useApi();
  const queryClient = useQueryClient();
  const { onModifiedDownloadSuccess } = useDownloadModifiedAsset();
  const { invalidateDownloadSizes } = useImageDownloadSizes();

  /**
   * Function to initiate a download request.
   * If the download is already complete in cache, it will return the cached data.
   * If not, it will initiate a new download request.
   */
  const downloadMutationFn = useCallback(
    async (
      downloadRequest: DownloadRequestBody
    ): Promise<DownloadComplete | DownloadNotComplete> => {
      const cacheKey = getDownloadKey(
        downloadRequest.genRequestId,
        downloadRequest.index
      );
      const cachedData = queryClient.getQueryData(cacheKey);

      if (isCompleteDownloadRequest(cachedData)) {
        return await Promise.resolve(cachedData);
      }

      queryClient.setQueryData(cacheKey, {
        status: DownloadStatus.DOWNLOADING,
      });

      const response = await downloadService.initiateDownload(downloadRequest);
      /**
       * If a new request (and not served from cache),
       * the download sizes cache and call the callback that might be declared in the consuming app.
       */
      onModifiedDownloadSuccess();

      invalidateDownloadSizes(
        downloadRequest.genRequestId,
        downloadRequest.index
      );
      return response;
    },
    [
      queryClient,
      downloadService,
      onModifiedDownloadSuccess,
      invalidateDownloadSizes,
    ]
  );

  return useMutation<
    DownloadComplete | DownloadNotComplete,
    Error,
    DownloadRequestBody
  >({
    mutationFn: downloadMutationFn,
    onSuccess: (
      response: DownloadComplete | DownloadNotComplete,
      request: DownloadRequestBody
    ) => {
      /** Updates the cache to most up to date value */
      queryClient.setQueryData(
        getDownloadKey(request.genRequestId, request.index),
        response
      );
      /** when downloadService.initiateDownload returns complete download data */
      if (isCompleteDownloadRequest(response)) {
        downloadFileFromUrl(document, response.url);
      }
    },
    onError: (_error, request: DownloadRequestBody) => {
      queryClient.setQueryData(
        getDownloadKey(request.genRequestId, request.index),
        FAILED_RESPONSE
      );
    },
  });
};

/**
 * @returns {Function} - marks a download as failed so the in-progress view
 * shows its error state. Used for failures that happen before the mutation runs
 * (e.g. the original-image download), which the mutation's own onError misses.
 */
export const useFailDownload = () => {
  const queryClient = useQueryClient();

  return ({ genRequestId, index }: { genRequestId: string; index: number }) => {
    queryClient.setQueryData(
      getDownloadKey(genRequestId, index),
      FAILED_RESPONSE
    );
  };
};

/**
 * @returns {Function} - function to invalidate a download query by genRequestId and index
 */
export const useInvalidateDownloadData = () => {
  const queryClient = useQueryClient();

  return ({ genRequestId, index }: { genRequestId: string; index: number }) => {
    queryClient.invalidateQueries({
      queryKey: getDownloadKey(genRequestId, index),
      refetchType: 'none',
    });
  };
};

/**
 * @returns {Function} - function to invalidate a download query by genRequestId and index
 */
export const useRemoveDownloadData = () => {
  const queryClient = useQueryClient();

  return ({ genRequestId, index }: { genRequestId: string; index: number }) => {
    queryClient.removeQueries({
      queryKey: getDownloadKey(genRequestId, index),
      exact: true,
    });
  };
};
